/**
 * AutomatedReportsTab - Daily/Weekly email reports
 * Schedule automated sales summaries
 */
import { useState, useEffect } from 'react';
import { 
  Mail, Calendar, Clock, Send, Plus, Trash2, 
  RefreshCw, Eye, Download, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function AutomatedReportsTab() {
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('daily');
  
  // New schedule form
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    report_type: 'daily_summary',
    recipients: '',
    schedule: 'daily',
    send_time: '08:00'
  });

  useEffect(() => {
    fetchReports();
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [dailyRes, weeklyRes] = await Promise.all([
        api.get('/admin/reports/daily'),
        api.get('/admin/reports/weekly')
      ]);
      
      setDailyReport(dailyRes.data);
      setWeeklyReport(weeklyRes.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    }
    setLoading(false);
  };

  const fetchSchedules = async () => {
    try {
      const res = await api.get('/admin/reports/schedules');
      setSchedules(res.data.schedules || []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  };

  const createSchedule = async () => {
    if (!newSchedule.name || !newSchedule.recipients) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await api.post('/admin/reports/schedules', {
        ...newSchedule,
        recipients: newSchedule.recipients.split(',').map(e => e.trim())
      });
      toast.success('Schedule created');
      fetchSchedules();
      setShowScheduleForm(false);
      setNewSchedule({
        name: '',
        report_type: 'daily_summary',
        recipients: '',
        schedule: 'daily',
        send_time: '08:00'
      });
    } catch (err) {
      toast.error('Failed to create schedule');
    }
  };

  const deleteSchedule = async (id) => {
    if (!confirm('Delete this schedule?')) return;
    
    try {
      await api.delete(`/admin/reports/schedules/${id}`);
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  const sendReportNow = async (type) => {
    const email = prompt('Enter email address to send report:');
    if (!email) return;
    
    try {
      await api.post('/admin/reports/send', {
        report_type: type,
        recipients: [email]
      });
      toast.success(`Report sent to ${email}`);
    } catch (err) {
      toast.error('Failed to send report');
    }
  };

  // Send the comprehensive daily sales report
  const [sendingSalesReport, setSendingSalesReport] = useState(false);
  const sendDailySalesReport = async () => {
    setSendingSalesReport(true);
    try {
      const res = await api.post('/admin/reports/daily-sales/send');
      toast.success(`Daily Sales Report sent to bulaccinocafe@gmail.com! Revenue: $${res.data.total_revenue?.toFixed(2) || '0.00'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send daily sales report');
    } finally {
      setSendingSalesReport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automated-reports-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
            <Mail className="w-6 h-6 text-rose-600 dark:text-rose-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Automated Reports</h2>
            <p className="text-muted-foreground text-sm">Daily and weekly email summaries</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={sendDailySalesReport}
            disabled={sendingSalesReport}
            className="rounded-full bg-amber-600 hover:bg-amber-700"
            data-testid="send-daily-sales-report-btn"
          >
            <Send className={`w-4 h-4 mr-2 ${sendingSalesReport ? 'animate-pulse' : ''}`} />
            {sendingSalesReport ? 'Sending...' : 'Send Daily Sales Report Now'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowScheduleForm(!showScheduleForm)}
            className="rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Schedule
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReports}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Daily Sales Report Info Box */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-amber-800 dark:text-amber-200 font-medium">Daily Sales Report (7:00 PM Fiji Time)</p>
            <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
              A comprehensive sales report is automatically sent to <strong>bulaccinocafe@gmail.com</strong> every day at 7:00 PM Fiji time.
              It includes sales by payment method, channel, category, hourly breakdown, staff performance, and voids/refunds.
            </p>
          </div>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeView === 'daily' ? 'default' : 'outline'}
          onClick={() => setActiveView('daily')}
          className="rounded-full"
        >
          Daily Report
        </Button>
        <Button
          variant={activeView === 'weekly' ? 'default' : 'outline'}
          onClick={() => setActiveView('weekly')}
          className="rounded-full"
        >
          Weekly Report
        </Button>
        <Button
          variant={activeView === 'schedules' ? 'default' : 'outline'}
          onClick={() => setActiveView('schedules')}
          className="rounded-full"
        >
          Schedules ({schedules.length})
        </Button>
      </div>

      {/* Daily Report Preview */}
      {activeView === 'daily' && dailyReport && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-foreground">
              Daily Report - {dailyReport.date}
            </h3>
            <Button size="sm" variant="outline" onClick={() => sendReportNow('daily')}>
              <Send className="w-4 h-4 mr-2" />
              Send Now
            </Button>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Total Sales</div>
              <div className="text-2xl font-bold text-foreground">
                ${dailyReport.summary?.total_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Orders</div>
              <div className="text-2xl font-bold text-foreground">
                {dailyReport.summary?.total_orders || 0}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Avg Order</div>
              <div className="text-2xl font-bold text-foreground">
                ${dailyReport.summary?.average_order?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Peak Hour</div>
              <div className="text-2xl font-bold text-foreground">
                {dailyReport.peak_hour || 'N/A'}:00
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-950/30 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="text-sm text-green-700 dark:text-green-300">Cash Sales</div>
              <div className="text-xl font-bold text-green-700 dark:text-green-300">
                ${dailyReport.summary?.cash_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <div className="text-sm text-blue-700 dark:text-blue-300">Card Sales</div>
              <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                ${dailyReport.summary?.card_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>

          {/* Top Items */}
          {dailyReport.top_items?.length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3">Top Selling Items</h4>
              <div className="space-y-2">
                {dailyReport.top_items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-foreground">{item.name}</span>
                    <div className="text-right">
                      <div className="font-medium text-foreground">{item.quantity} sold</div>
                      <div className="text-sm text-muted-foreground">${item.revenue?.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weekly Report Preview */}
      {activeView === 'weekly' && weeklyReport && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-foreground">
              Weekly Report - {weeklyReport.period?.start} to {weeklyReport.period?.end}
            </h3>
            <Button size="sm" variant="outline" onClick={() => sendReportNow('weekly')}>
              <Send className="w-4 h-4 mr-2" />
              Send Now
            </Button>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Total Sales</div>
              <div className="text-2xl font-bold text-foreground">
                ${weeklyReport.summary?.total_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Total Orders</div>
              <div className="text-2xl font-bold text-foreground">
                {weeklyReport.summary?.total_orders || 0}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Avg Daily Sales</div>
              <div className="text-2xl font-bold text-foreground">
                ${weeklyReport.summary?.average_daily_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Avg Order</div>
              <div className="text-2xl font-bold text-foreground">
                ${weeklyReport.summary?.average_order?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>

          {/* Comparison */}
          {weeklyReport.comparison && (
            <div className={`p-4 rounded-xl mb-6 ${
              weeklyReport.comparison.trend === 'up' 
                ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800'
                : weeklyReport.comparison.trend === 'down'
                ? 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800'
                : 'bg-muted/30'
            }`}>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-bold ${
                  weeklyReport.comparison.trend === 'up' ? 'text-green-600 dark:text-green-400' :
                  weeklyReport.comparison.trend === 'down' ? 'text-red-600 dark:text-red-400' :
                  'text-foreground'
                }`}>
                  {weeklyReport.comparison.trend === 'up' ? '+' : ''}{weeklyReport.comparison.change_percentage}%
                </span>
                <span className="text-muted-foreground">vs previous week</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Previous: ${weeklyReport.comparison.previous_week_sales?.toFixed(2)}
              </div>
            </div>
          )}

          {/* Best/Worst Days */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 dark:bg-green-950/30 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="text-sm text-green-700 dark:text-green-300 mb-1">Best Day</div>
              <div className="font-medium text-green-700 dark:text-green-300">
                {weeklyReport.best_day?.date}
              </div>
              <div className="text-lg font-bold text-green-700 dark:text-green-300">
                ${weeklyReport.best_day?.sales?.toFixed(2)} ({weeklyReport.best_day?.orders} orders)
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
              <div className="text-sm text-amber-700 dark:text-amber-300 mb-1">Slowest Day</div>
              <div className="font-medium text-amber-700 dark:text-amber-300">
                {weeklyReport.worst_day?.date}
              </div>
              <div className="text-lg font-bold text-amber-700 dark:text-amber-300">
                ${weeklyReport.worst_day?.sales?.toFixed(2)} ({weeklyReport.worst_day?.orders} orders)
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedules */}
      {activeView === 'schedules' && (
        <div className="space-y-4">
          {/* New Schedule Form */}
          {showScheduleForm && (
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-heading text-lg text-foreground mb-4">New Report Schedule</h3>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Schedule Name</label>
                  <Input
                    placeholder="e.g., Morning Summary"
                    value={newSchedule.name}
                    onChange={(e) => setNewSchedule({...newSchedule, name: e.target.value})}
                    className="bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Report Type</label>
                  <select
                    value={newSchedule.report_type}
                    onChange={(e) => setNewSchedule({...newSchedule, report_type: e.target.value})}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                  >
                    <option value="daily_summary">Daily Summary</option>
                    <option value="weekly_summary">Weekly Summary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Recipients (comma-separated)</label>
                  <Input
                    placeholder="email1@example.com, email2@example.com"
                    value={newSchedule.recipients}
                    onChange={(e) => setNewSchedule({...newSchedule, recipients: e.target.value})}
                    className="bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Send Time</label>
                  <Input
                    type="time"
                    value={newSchedule.send_time}
                    onChange={(e) => setNewSchedule({...newSchedule, send_time: e.target.value})}
                    className="bg-background"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={createSchedule}>
                  <Check className="w-4 h-4 mr-2" />
                  Create Schedule
                </Button>
                <Button variant="outline" onClick={() => setShowScheduleForm(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Schedule List */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading text-lg text-foreground mb-4">Active Schedules</h3>
            {schedules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No schedules configured yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowScheduleForm(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Schedule
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {schedules.map((schedule) => (
                  <div key={schedule.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        schedule.report_type === 'daily_summary' 
                          ? 'bg-blue-100 dark:bg-blue-900/30' 
                          : 'bg-purple-100 dark:bg-purple-900/30'
                      }`}>
                        <Mail className={`w-5 h-5 ${
                          schedule.report_type === 'daily_summary' 
                            ? 'text-blue-600 dark:text-blue-400' 
                            : 'text-purple-600 dark:text-purple-400'
                        }`} />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{schedule.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {schedule.schedule} at {schedule.send_time} | {schedule.recipients?.length || 0} recipients
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        schedule.is_active 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}>
                        {schedule.is_active ? 'Active' : 'Paused'}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteSchedule(schedule.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
